import React from 'react'
import { Link } from 'react-router-dom'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import {
  AlertTriangle,
  X,
  ArrowRight,
  Info,
  Sparkles
} from 'lucide-react'

interface UpgradePromptProps {
  type: 'modal' | 'banner' | 'inline'
  title: string
  message: string
  feature?: string
  currentTier?: string
  onClose?: () => void
  recommendations?: Array<{
    tier: string
    displayName: string
    price: number
    features: string[]
  }>
}

const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  type,
  title,
  message,
  feature,
  currentTier,
  onClose,
  recommendations = []
}) => {
  const billingUrl = `/billing${feature ? `?feature=${feature}` : ''}`

  if (type === 'modal') {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-20">
        <Card className="w-full max-w-md mx-4 relative">
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          )}

          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-2">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            </div>
            <CardTitle className="text-lg">{title}</CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">{message}</p>

            {recommendations.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-foreground">Recommended Plans:</p>
                <div className="space-y-2">
                  {recommendations.slice(0, 2).map((rec, index) => (
                    <div
                      key={index}
                      className="p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-foreground">{rec.displayName}</span>
                        <Badge variant="secondary" className="bg-primary/10 text-primary">
                          ${rec.price}/mo
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {rec.features.slice(0, 2).join(' • ')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2 pt-2">
              <Button asChild className="w-full">
                <Link to={billingUrl}>
                  <Sparkles className="h-4 w-4 mr-2" />
                  View Plans
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
              {onClose && (
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="w-full"
                >
                  Maybe Later
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (type === 'banner') {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-yellow-800">{title}</h3>
            <p className="mt-1 text-sm text-yellow-700">{message}</p>
            <div className="mt-3">
              <Button asChild size="sm" className="bg-yellow-700 hover:bg-yellow-800">
                <Link to={billingUrl}>
                  Upgrade Now
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
          {onClose && (
            <div className="ml-auto pl-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100"
                onClick={onClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Inline type
  return (
    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <Info className="h-5 w-5 text-primary" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{message}</p>
          <div className="mt-3">
            <Button asChild size="sm">
              <Link to={billingUrl}>
                View Plans
                <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UpgradePrompt
